import { useParams, Link, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../lib/Seo";
import RevealText from "../lib/RevealText";
import Reveal from "../lib/Reveal";
import JourneyCard from "../components/JourneyCard";
import { findItinerary, itineraries } from "../data/itineraries";
import { ArrowUpRight, MapPin, Compass, Home as HomeIcon } from "lucide-react";

export default function JourneyDetail() {
  const { slug } = useParams();
  const journey = findItinerary(slug);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  if (!journey) return <Navigate to="/journeys" replace />;

  const related = itineraries.filter((i) => i.slug !== journey.slug).slice(0, 3);

  const schema = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    name: journey.name,
    description: journey.seoDescription,
    touristType: "Luxury safari",
    itinerary: journey.days.map((d) => ({
      "@type": "Place",
      name: d.title,
      description: d.detail,
    })),
    provider: {
      "@type": "TravelAgency",
      name: "Njia Safaris",
      url: "https://njiasafaris.com",
    },
  };

  return (
    <>
      <Seo
        title={journey.seoTitle}
        description={journey.seoDescription}
        image={journey.hero}
        path={`/journeys/${journey.slug}`}
        schema={schema}
      />
      <Nav />

      {/* HERO */}
      <section
        ref={heroRef}
        className="relative h-[100svh] overflow-hidden section-dark"
        data-testid="journey-hero"
      >
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src={journey.hero} alt={journey.name} className="w-full h-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/25 to-black/70" />
        <div className="grain-overlay" />

        <div className="relative z-10 h-full container-x flex flex-col justify-end pb-16 md:pb-24">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-8">
            <Link to="/journeys" className="link-under text-[color:var(--ivory)]" data-testid="journey-back">
              ← All journeys
            </Link>
            <span className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-3)]">
              {journey.country}
            </span>
            <span className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--sand)]">
              {journey.duration}
            </span>
          </div>
          <RevealText
            as="h1"
            className="h-display text-[13vw] md:text-[9vw] text-[color:var(--ivory)] max-w-6xl"
            lines={[journey.name]}
          />
          <p className="mt-6 font-italic-display text-2xl md:text-3xl text-[color:var(--gold-3)] max-w-3xl">
            {journey.tagline}
          </p>
        </div>
      </section>

      {/* INTRO + ROUTE */}
      <section className="section-y container-x" data-testid="journey-intro">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <p className="eyebrow mb-4">The journey</p>
            <p className="font-display text-3xl md:text-4xl leading-tight text-[color:var(--ink)]">
              {journey.intro}
            </p>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <div className="grid grid-cols-2 gap-8 mb-10">
              <div>
                <p className="eyebrow mb-2">Level</p>
                <p className="font-display text-xl leading-snug">{journey.level}</p>
              </div>
              <div>
                <p className="eyebrow mb-2">Duration</p>
                <p className="font-display text-xl leading-snug">{journey.duration}</p>
              </div>
            </div>
            <p className="eyebrow mb-4">Route</p>
            <div className="route-line">
              {journey.route.map((r, i) => (
                <div key={r} className="route-line">
                  <span className="route-pill">{r}</span>
                  {i < journey.route.length - 1 && <span className="route-dot" />}
                </div>
              ))}
            </div>
            <Link to="/contact" state={{ journey: journey.name }} className="btn-gold mt-10" data-testid="journey-enquire-top">
              Enquire about this journey
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="section-forest py-24 md:py-32" data-testid="journey-highlights">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="eyebrow mb-4">Journey highlights</p>
              <RevealText
                as="h2"
                className="h-display text-4xl md:text-5xl text-[color:var(--ivory)]"
                lines={["What you", <em key="e" className="italic text-[color:var(--gold-3)]">carry home.</em>]}
              />
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <ul className="divide-y divide-[color:var(--line-invert)]">
                {journey.highlights.map((h, i) => (
                  <Reveal as="li" key={h} delay={i * 0.05}>
                    <div className="flex items-start gap-6 py-6">
                      <span className="mono text-[11px] uppercase tracking-[0.28em] text-[color:var(--gold-3)] pt-1 shrink-0 w-10">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <p className="font-display text-2xl md:text-3xl leading-tight text-[color:var(--ivory)]">
                        {h}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* DAY BY DAY */}
      <section className="section-y container-x" data-testid="journey-days">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-4">
            <p className="eyebrow mb-4">Day by day</p>
            <RevealText
              as="h2"
              className="h-display text-4xl md:text-5xl"
              lines={["The path,", <em key="e" className="italic text-[color:var(--forest)]">day by day.</em>]}
            />
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-lg leading-relaxed max-w-xl">
              Every day is composed to your appetite. Below is the shape of the journey — the sequence,
              the pace, the arrivals. The hours in between are yours to fill.
            </p>
          </div>
        </div>

        <div>
          {journey.days.map((d, i) => (
            <Reveal key={d.day} delay={i * 0.03}>
              <div className="grid grid-cols-12 gap-6 md:gap-10 py-8 border-t border-[color:var(--line)]">
                <div className="col-span-3 md:col-span-2">
                  <span className="mono text-[11px] uppercase tracking-[0.28em] text-[color:var(--forest)]">
                    Day {String(d.day).padStart(2, "0")}
                  </span>
                </div>
                <div className="col-span-9 md:col-span-4">
                  <h3 className="font-display text-2xl md:text-3xl leading-tight">{d.title}</h3>
                </div>
                <div className="col-span-12 md:col-span-6">
                  <p className="text-[15px] leading-relaxed text-[color:var(--ink)]/85">{d.detail}</p>
                </div>
              </div>
            </Reveal>
          ))}
          <div className="hairline" />
        </div>
      </section>

      {/* LODGES */}
      <section className="section-sand section-y" data-testid="journey-lodges">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-4">
              <p className="eyebrow mb-4">Camps & lodges</p>
              <RevealText
                as="h2"
                className="h-display text-4xl md:text-5xl"
                lines={["Where you", <em key="e" className="italic text-[color:var(--forest)]">wake.</em>]}
              />
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <p className="text-lg leading-relaxed max-w-xl">
                We work only with camps we know personally. Every lodge below has been visited, tested and
                trusted — some for a decade or more.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {journey.lodges.map((l, i) => (
              <Reveal key={l.name} delay={i * 0.05}>
                <div className="border-t border-[color:var(--ink)] pt-4">
                  <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--forest)] mb-3">
                    {String(i + 1).padStart(2, "0")} · {l.region}
                  </p>
                  <h3 className="font-display text-2xl md:text-3xl leading-tight">{l.name}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ENQUIRE CTA */}
      <section className="section-dark py-24 md:py-32" data-testid="journey-cta">
        <div className="container-x grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="eyebrow eyebrow-invert mb-6">Begin</p>
            <RevealText
              as="h2"
              className="h-display text-5xl md:text-7xl text-[color:var(--ivory)]"
              lines={["Ready to walk", <em key="e" className="italic text-[color:var(--gold-3)]">this path?</em>]}
            />
          </div>
          <div className="md:col-span-5">
            <p className="max-w-md text-lg leading-relaxed text-[color:var(--sand)] mb-8">
              Every itinerary is composed by hand around your dates, pace and appetite for wildness.
              We reply within one working day.
            </p>
            <Link to="/contact" state={{ journey: journey.name }} className="btn-gold" data-testid="journey-enquire-bottom">
              Enquire about {journey.name}
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section className="section-y container-x" data-testid="journey-related">
        <div className="flex items-end justify-between gap-6 mb-14">
          <div>
            <p className="eyebrow mb-4">Also worth walking</p>
            <RevealText
              as="h2"
              className="h-display text-4xl md:text-5xl"
              lines={["Other paths"]}
            />
          </div>
          <Link to="/journeys" className="link-under">
            All ten →
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-8 md:gap-10">
          {related.map((j, i) => (
            <JourneyCard key={j.slug} journey={j} index={i} />
          ))}
        </div>
      </section>

      <Footer />
    </>
  );
}
